/**
 * Environment Selector Component
 * Searchable combined retailer+environment selector using Popover + Command (cmdk).
 * Displays all retailer/environment combinations in a single grouped dropdown.
 * @packageDocumentation
 */

import { useTranslation } from 'react-i18next';
import { Loader2, Building2, ChevronDown } from 'lucide-react';
import { lazy, useMemo, useState, Suspense, useCallback } from 'react';

import { cn } from '@/lib/utils';
import { type Environment, useEnvironmentStore, type RetailerWithEnvironments } from '@/stores';

import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { ENV_BADGE_STYLE, ENV_SHORT_NAMES } from './EnvironmentSelector.constants';

const EnvironmentSelectorList = lazy(() =>
  import('./EnvironmentSelectorList').then((m) => ({ default: m.EnvironmentSelectorList })),
);

/**
 * Props for the EnvironmentSelector component.
 */
export interface EnvironmentSelectorProps {
  className?: string;
  isLoading?: boolean;
  retailers: RetailerWithEnvironments[];
  onEnvironmentChange?: (retailerCode: string, environment: Environment) => void;
}

/**
 * Combined retailer and environment selector using a searchable command menu.
 * All retailer/environment combinations are shown in a single grouped list,
 * filterable by typing. Replaces the previous two-dropdown pattern.
 *
 * @param { EnvironmentSelectorProps } props Component props.
 * @returns { JSX.Element } The environment selector element.
 */
export const EnvironmentSelector = ({
  retailers,
  className,
  isLoading = false,
  onEnvironmentChange,
}: EnvironmentSelectorProps) => {
  const { t } = useTranslation();
  const { currentEnvSummary, currentEnvironment, currentRetailerCode, setCurrentEnvironment } = useEnvironmentStore();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const currentValue = useMemo(() => {
    if (!currentRetailerCode || !currentEnvironment) {
      return '';
    }

    return `${currentRetailerCode}:${currentEnvironment}`;
  }, [currentRetailerCode, currentEnvironment]);

  const handleSelect = useCallback(
    (compositeValue: string) => {
      const [retailerCode, environment] = compositeValue.split(':');

      if (retailerCode && environment) {
        setCurrentEnvironment(retailerCode, environment as Environment);
        onEnvironmentChange?.(retailerCode, environment as Environment);
      }

      setOpen(false);
    },
    [setCurrentEnvironment, onEnvironmentChange],
  );

  const handleOpenChange = useCallback((isOpen: boolean) => {
    setOpen(isOpen);

    if (!isOpen) {
      setSearch('');
    }
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
        <span>{t('status.loadingEnvironments')}</span>
      </div>
    );
  }

  if (retailers.length === 0) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
        <Building2 className="size-4" />
        <span>{t('status.noEnvironments')}</span>
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-label={t('aria.selectRetailerEnvironment')}
          className={cn(
            'flex h-9 w-full items-center gap-2 rounded-lg border border-border/60 bg-muted/40 px-3 text-sm transition-colors hover:bg-muted/70',
            className,
          )}
        >
          <Building2 className="size-4 shrink-0 text-muted-foreground" />
          {currentEnvSummary && currentEnvironment ? (
            <span className="flex flex-1 items-center justify-between truncate text-left">
              <span className="truncate">{currentEnvSummary.retailerName}</span>
              <span
                className={cn(
                  'inline-flex shrink-0 items-center rounded-md px-2 py-0.5 text-[11px] font-semibold',
                  ENV_BADGE_STYLE,
                )}
              >
                {ENV_SHORT_NAMES[currentEnvironment]}
              </span>
            </span>
          ) : (
            <span className="flex-1 truncate text-left">{t('environmentSelector.selectContext')}</span>
          )}
          <ChevronDown className="size-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Suspense
          fallback={
            <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              <span>{t('status.loadingEnvironments')}</span>
            </div>
          }
        >
          <EnvironmentSelectorList
            retailers={retailers}
            search={search}
            onSearchChange={setSearch}
            currentValue={currentValue}
            onSelect={handleSelect}
          />
        </Suspense>
      </PopoverContent>
    </Popover>
  );
};
